import { Avatar, AvatarFallback } from "@/components/ui/library/avatar";
import { Button } from "@/components/ui/button";
import { Phone, Mail, Pencil, X, Star, Check, Trash2, MoreVertical, AlertTriangle, Archive, ArchiveRestore, Flag, Copy, Bot, GitMerge } from "lucide-react";
import { getPriorityColors } from "@/features/leads/utilities/Prioritycolors";
import { useAppSelector } from "@/stores/hook";
import { setIsEditMode, setLeadCurrentTab } from "@/features/leads/stores/leadFunctionalSlice";
import { useDispatch } from "react-redux";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useLeadMutations } from "@/features/leads/api/useLeadMutations";
import { useChildMutations } from "@/features/leads/api/useChildMutations";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import usePipeline from "@/features/stage/api/usePipeline";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Can } from "@/rbac/components/Can";
import { usePermissions } from "@/rbac/usePermissions";
import { useLeadFormLockedFields } from "@/features/leads/api/useLeadFormConfig";
import { toast } from "sonner";
import { useBulkArchive } from "@/features/leads/api/useArchive";
import { selectCanArchiveLeads } from "@/rbac/rbacSlice";
import VisibilityInfo from "./VisibilityInfo";
import LeadChatLinkButton from "./LeadChatLinkButton";
import MergeLeadDialog from "./MergeLeadDialog";
import { formatLeadPhone } from "@/features/leads/utilities/formatLeadPhone";
import { useIsAIEnabled } from "@/features/ai/hooks/useIsAIEnabled";
import { openVoiceCall } from "@/features/ai/stores/aiChatSlice";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface LeadHeaderProps {
  onClose?: () => void;
}

export default function LeadHeader({ onClose }: LeadHeaderProps) {
  const { isEditMode, currentTab } = useAppSelector(
    (state) => state.leadFunctional
  );
  const userPriorityColors = useAppSelector(
    (state) => state.managePreferences.priorityColors
  );
  const dispatch = useDispatch();
  const { lead, child, isChildActive } = useActiveDataMutation();
  const { canDeadline, canStage, bypassesRbacChecks } = usePermissions();
  const { isViewLocked } = useLeadForm();
  const { isFieldLocked, showLeadId } = useLeadFormLockedFields();
  // "Call with Xale AI" — the AI telecaller phones this lead (Super Admins
  // with AI enabled; the server enforces the same gate on the socket).
  const aiEnabled = useIsAIEnabled();
  const startAICall = useCallback(() => {
    const id = Number(lead?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    dispatch(openVoiceCall({ mode: "telecaller", leadId: id, leadName: String(lead?.name ?? "") }));
  }, [dispatch, lead?.id, lead?.name]);

  // "Copied" feedback on the ID chip — flips back after a moment so the chip
  // can be copied again and the label never sticks on a stale state.
  const [idCopied, setIdCopied] = useState(false);
  const copyLeadId = useCallback(async () => {
    const id = lead?.id;
    if (id == null) return;
    try {
      await navigator.clipboard.writeText(String(id));
      setIdCopied(true);
      toast.success(`Lead ID ${id} copied`);
      window.setTimeout(() => setIdCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — select the ID and copy it manually");
    }
  }, [lead?.id]);
  const { updateLead, deleteLead } = useLeadMutations();
  const { deleteChild } = useChildMutations();
  const archiveMutation = useBulkArchive();
  // ARCHIVING is admin-scoped — the server rejects everyone else
  // (`leadService.bulkArchive` → `ensureAtLeastTenantAdmin` on the archive
  // arm), so hide the item rather than leave it to 403. RESTORE is NOT
  // gated (same as the Archived leads panel), so an already-archived lead
  // still shows "Restore from Archive" to any role that can edit leads.
  const canArchiveLeads = useAppSelector(selectCanArchiveLeads);
  const priority = lead?.priority || "high";
  const isArchived = Boolean((lead as any)?.isArchived);
  const canToggleArchive = canArchiveLeads || isArchived;

  const priorityColors = priority
    ? getPriorityColors(priority, userPriorityColors)
    : null;

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(lead?.name ?? "");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // Merge another lead into this one (owner, 2026-10-19).
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteDealDialogOpen, setIsDeleteDealDialogOpen] = useState(false);
  const [isDeletingDeal, setIsDeletingDeal] = useState(false);

  // "Delete Deal" — only when a deal is the active context, gated by the
  // role wizard's per-stage "Choices → Delete choices" toggle on the DEAL's
  // stage (server enforces the same — ChildPolicy.ensureDelete).
  const activeDealId =
    isChildActive && child?.id != null ? Number(child.id) : null;
  const activeDealStageId = Number(
    (child as any)?.stageId ?? (child as any)?.stage?.id,
  );
  const canDeleteActiveDeal =
    activeDealId != null &&
    (bypassesRbacChecks ||
      (Number.isFinite(activeDealStageId) &&
        canStage(activeDealStageId, "choice_management", "delete")));

  // Deleting the LAST deal drops the lead out of the deal stages: the server
  // (`syncLeadStageToHighestDeal`, onNoDeals "preDealStage") moves it to the
  // last stage that needs no deal — the highest non-deal stage below the
  // pipeline's first deal stage. Mirror that here so the confirm can say so.
  const { pipeline: pipelineStages } = usePipeline();
  const isLastDeal = activeDealId != null && (lead?.child?.length ?? 0) <= 1;
  const lastDealFallbackStageName = useMemo(() => {
    if (!isLastDeal || !Array.isArray(pipelineStages)) return null;
    const stages = [...pipelineStages].sort(
      (a: any, b: any) => Number(a?.order ?? 0) - Number(b?.order ?? 0),
    );
    const firstDealIdx = stages.findIndex((s: any) => s?.isChild);
    if (firstDealIdx <= 0) return null;
    const leadStage = stages.find(
      (s: any) => Number(s?.id) === Number((lead as any)?.stageId ?? (lead as any)?.stage?.id),
    );
    // Lead already before the deal stages → it stays where it is.
    if (
      leadStage &&
      Number(leadStage.order) < Number(stages[firstDealIdx].order)
    ) {
      return null;
    }
    return (stages[firstDealIdx - 1]?.name as string | undefined) ?? null;
  }, [isLastDeal, pipelineStages, lead]);

  const handleDeleteDeal = useCallback(async () => {
    if (activeDealId == null) return;
    setIsDeletingDeal(true);
    try {
      await deleteChild.mutateAsync(activeDealId);
      setIsDeleteDealDialogOpen(false);
    } finally {
      setIsDeletingDeal(false);
    }
  }, [activeDealId, deleteChild]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditedName(lead?.name ?? "");
  }, [lead?.name]);

  useEffect(() => {
    if (isEditingName) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditingName]);

  useEffect(() => {
    return () => {
      dispatch(setIsEditMode(false));
    };
  }, []);

  const handleEditMode = () => {
    if (isViewLocked) return;
    dispatch(setIsEditMode(true));
  };

  const handleNameClick = () => {
    if (isViewLocked) return;
    // Click-to-edit has no edit mode to disable, so the name lock is gated
    // here. The server rejects the change regardless.
    if (isFieldLocked("basic:name")) {
      toast.error("Full Name is locked for editing by your administrator");
      return;
    }
    setIsEditingName(true);
  };

  const handleSaveName = useCallback(async () => {
    const trimmed = editedName.trim();
    if (!trimmed || trimmed === lead?.name) {
      setIsEditingName(false);
      setEditedName(lead?.name ?? "");
      return;
    }
    try {
      await updateLead.mutateAsync({ id: lead?.id, data: { name: trimmed } });
      setIsEditingName(false);
    } catch {
      setEditedName(lead?.name ?? "");
      setIsEditingName(false);
    }
  }, [editedName, lead?.id, lead?.name, updateLead]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSaveName();
    if (e.key === "Escape") {
      setEditedName(lead?.name ?? "");
      setIsEditingName(false);
    }
  };

  const nameIsDirty =
    editedName.trim() !== "" && editedName.trim() !== lead?.name;

  const childCount = lead?.child?.length ?? 0;
  const noteCount = lead?.notes?.length ?? 0;
  // No-branch leads are treated like normal leads for follow-ups, so the
  // header badge reflects their real count too.
  const followUpCount = lead?.followUpDates?.length ?? 0;
  const whatsappChatCount = lead?.whatsappChats?.length ?? 0;

  // Most-pressing pending deadline for the prominent header pill.
  // Pending only; sort so the most "past nudge" (highest elapsed - warningDays)
  // bubbles to the front. Untriggered ones fall back to creation order.
  const headerDeadline = useMemo(() => {
    const list: any[] = lead?.deadlineDates || [];
    if (!list.length) return null;
    const pending = list.filter((d) => d?.status === "Pending");
    if (!pending.length) return null;
    const daysSince = (iso: string | null | undefined): number | null => {
      if (!iso) return null;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      d.setHours(0, 0, 0, 0);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return Math.max(0, Math.floor((now.getTime() - d.getTime()) / 86400000));
    };
    return [...pending].sort((a, b) => {
      const aw = Number(a?.warningDays) || 0;
      const bw = Number(b?.warningDays) || 0;
      const ae = (daysSince(a?.triggeredAt) ?? 0) - aw;
      const be = (daysSince(b?.triggeredAt) ?? 0) - bw;
      return be - ae;
    })[0];
  }, [lead?.deadlineDates]);

  /**
   * Per-stage `lead_deadline` view gate. The header pill is a deadline
   * preview / shortcut to the Deadlines tab — if the user can't view
   * deadlines on the lead's current stage, the pill is suppressed.
   */
  const leadStageIdNum = useMemo(() => {
    const raw = (lead as any)?.stage?.id ?? (lead as any)?.stageId;
    if (raw == null || raw === "") return NaN;
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  }, [lead]);
  const canViewDeadline = canDeadline(leadStageIdNum, "view");

  const headerDeadlineMeta = useMemo(() => {
    if (!headerDeadline) return null;
    if (!canViewDeadline) return null;
    const target =
      headerDeadline?.toStatus?.name ||
      headerDeadline?.toStatus?.statusName ||
      "Milestone";
    const wd =
      headerDeadline?.warningDays != null
        ? Number(headerDeadline.warningDays)
        : null;
    const triggeredAt = headerDeadline?.triggeredAt as string | null;
    let elapsed: number | null = null;
    if (triggeredAt) {
      const d = new Date(triggeredAt);
      if (!Number.isNaN(d.getTime())) {
        d.setHours(0, 0, 0, 0);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        elapsed = Math.max(
          0,
          Math.floor((now.getTime() - d.getTime()) / 86400000)
        );
      }
    }
    const armed = elapsed != null;
    const pastNudge = armed && wd != null && (elapsed as number) >= wd;
    return { target, wd, elapsed, armed, pastNudge };
  }, [headerDeadline, canViewDeadline]);

  const handleOpenDeadlinesTab = useCallback(() => {
    dispatch(setLeadCurrentTab("Deadlines"));
  }, [dispatch]);

  const handleDeleteLead = useCallback(async () => {
    if (!lead?.id) return;
    setIsDeleting(true);
    try {
      await deleteLead.mutateAsync(Number(lead.id));
      setIsDeleteDialogOpen(false);
      onClose?.();
    } finally {
      setIsDeleting(false);
    }
  }, [lead?.id, deleteLead, onClose]);

  const handleToggleArchive = useCallback(async () => {
    if (!canToggleArchive) return;
    if (!lead?.id || archiveMutation.isPending) return;
    await archiveMutation.mutateAsync({
      leads: [Number(lead.id)],
      archive: !isArchived,
    });
    if (!isArchived) {
      onClose?.();
    }
  }, [canToggleArchive, lead?.id, isArchived, archiveMutation, onClose]);

  return (
    <div className="flex gap-3 items-center w-full">
      <div className="flex-1 flex gap-2.5 items-center min-w-0">
        <div className="relative shrink-0">
          <Avatar
            className={`size-10 shrink-0 rounded-full ${
              priorityColors && "border-[2.5px]"
            }`}
            style={
              priorityColors
                ? { borderColor: priorityColors.border }
                : undefined
            }
          >
            <AvatarFallback className="bg-[var(color-toggle-inactive)] text-[15px] font-medium tracking-[-0.27px]">
              {lead?.name?.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>

          {priorityColors && (
            <span className="absolute -top-0.5 -right-0.5 flex h-[12px] w-[12px] items-center justify-center rounded-full bg-white shadow-sm">
              <span
                className="h-[7px] w-[7px] rounded-full"
                style={{ backgroundColor: priorityColors.dot }}
              />
            </span>
          )}
        </div>

        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex gap-2 items-center">
            {isEditingName ? (
              <div className="flex items-center gap-1">
                <input
                  ref={inputRef}
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={() => {
                    if (!nameIsDirty) {
                      setIsEditingName(false);
                      setEditedName(lead?.name ?? "");
                    }
                  }}
                  className="text-[18px] font-medium text-black tracking-[-0.54px] leading-normal bg-transparent border-b border-[#267355] outline-none min-w-0 max-w-[200px] py-0"
                />
                {nameIsDirty && (
                  <button
                    onClick={handleSaveName}
                    disabled={updateLead.isPending}
                    className="size-6 rounded-full bg-[#267355] hover:bg-[#1e5c44] text-white flex items-center justify-center shrink-0 transition-colors"
                  >
                    <Check className="size-3.5" strokeWidth={2.5} />
                  </button>
                )}
              </div>
            ) : (
              <h4
                className={`!text-[18px] font-medium text-black tracking-[-0.54px] leading-normal ${
                  isViewLocked
                    ? "cursor-default"
                    : "cursor-pointer hover:text-[#267355] transition-colors"
                }`}
                onClick={handleNameClick}
                title={isViewLocked ? undefined : "Click to edit name"}
              >
                {lead?.name}
              </h4>
            )}
            {!isEditingName && lead?.leadScore ? (
              <div className="flex items-center gap-1 pl-1.5">
                <Star
                  className="size-4 text-[#f08614] fill-[#f08614]"
                  strokeWidth={1.5}
                />
                <span className="text-[13px] font-medium text-[#f08614] tracking-[-0.52px]">
                  {lead?.leadScore}
                </span>
              </div>
            ) : null}
          </div>

          <div className="flex gap-3 items-center flex-wrap">
            {showLeadId && lead?.id != null ? (
              // Click-to-copy: the ID is what a colleague pastes into the leads
              // search to land on this exact lead, so it must be copyable in one
              // click and SAY it was copied — a silent copy reads as a dead label.
              <button
                type="button"
                onClick={copyLeadId}
                title={idCopied ? "Copied" : "Click to copy the lead ID"}
                aria-label={`Lead ID ${lead.id} — click to copy`}
                className="flex gap-1 items-center rounded-md px-1 -mx-1 hover:bg-[var(--color-black-1)] transition-colors cursor-pointer"
              >
                <span className="text-[13px] font-medium text-[var(--color-black-6)] tracking-[-0.26px]">
                  ID
                </span>
                <span className="text-[13px] text-[var(--color-black-7)] tracking-[-0.26px] tabular-nums">
                  {lead.id}
                </span>
                {idCopied ? (
                  <span className="flex items-center gap-0.5 text-[11px] font-medium text-[var(--color-success)]">
                    <Check className="size-3" strokeWidth={2} />
                    Copied
                  </span>
                ) : (
                  <Copy className="size-3 text-[var(--color-black-5)]" strokeWidth={1.5} />
                )}
              </button>
            ) : null}
            <div className="flex gap-1 items-center">
              <Phone
                className="size-3.5 text-[var(--color-black-6)]"
                strokeWidth={1.5}
              />
              <span className="text-[13px] text-[var(--color-black-7)] tracking-[-0.26px]">
                {formatLeadPhone(lead?.phone, lead?.phoneCountryCode)}
              </span>
            </div>

            <div className="flex gap-1 items-center">
              <Mail
                className="size-3.5 text-[var(--color-black-6)]"
                strokeWidth={1.5}
              />
              <span className="text-[13px] text-[var(--color-black-7)] tracking-[-0.26px]">
                {lead?.email}
              </span>
            </div>

            {headerDeadlineMeta ? (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleOpenDeadlinesTab}
                    className={`group/dl inline-flex items-center gap-1.5 h-[22px] pl-1.5 pr-2 rounded-full border transition-colors cursor-pointer ${
                      headerDeadlineMeta.pastNudge
                        ? "bg-amber-50 border-amber-200 hover:bg-amber-100"
                        : "bg-[var(--color-success)]/8 border-[var(--color-success)]/25 hover:bg-[var(--color-success)]/14"
                    }`}
                  >
                    <Flag
                      className={`size-3 ${
                        headerDeadlineMeta.pastNudge
                          ? "text-[#92400e]"
                          : "text-[var(--color-success)]"
                      }`}
                      strokeWidth={2.5}
                    />
                    <span
                      className={`text-[11.5px] font-medium tracking-[-0.2px] truncate max-w-[180px] ${
                        headerDeadlineMeta.pastNudge
                          ? "text-[#92400e]"
                          : "text-[var(--color-success)]"
                      }`}
                    >
                      Deadline · {headerDeadlineMeta.target}
                    </span>
                    {headerDeadlineMeta.wd != null ? (
                      <span
                        className={`text-[10.5px] font-semibold tabular-nums tracking-[-0.2px] pl-1 ml-0.5 border-l ${
                          headerDeadlineMeta.pastNudge
                            ? "text-[#92400e] border-amber-300/70"
                            : "text-[var(--color-success)] border-[var(--color-success)]/30"
                        }`}
                      >
                        {headerDeadlineMeta.armed
                          ? `${headerDeadlineMeta.elapsed}/${headerDeadlineMeta.wd}d`
                          : `${headerDeadlineMeta.wd}d`}
                      </span>
                    ) : null}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="max-w-[260px]">
                  <div className="text-[12px] leading-snug">
                    <div className="font-semibold mb-0.5">
                      {headerDeadlineMeta.pastNudge
                        ? "Milestone past nudge"
                        : "Pending milestone"}
                    </div>
                    <div className="text-[var(--color-black-7)]">
                      Auto-completes when this lead reaches{" "}
                      <span className="font-medium text-[var(--color-text-primary)]">
                        {headerDeadlineMeta.target}
                      </span>
                      .
                    </div>
                    {headerDeadlineMeta.wd != null ? (
                      <div className="mt-1 text-[var(--color-black-6)]">
                        {headerDeadlineMeta.armed
                          ? `Day ${headerDeadlineMeta.elapsed} of ${headerDeadlineMeta.wd} since timer started`
                          : `Timer arms once the lead reaches the from-status (${headerDeadlineMeta.wd}d nudge)`}
                      </div>
                    ) : null}
                    <div className="mt-1 text-[var(--color-success)] font-medium">
                      Click to open the Deadlines tab →
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex gap-1.5 items-center shrink-0">
        {!isEditMode && (
          <VisibilityInfo
            mode={isChildActive ? "child" : "lead"}
            id={isChildActive ? child?.id : lead?.id}
          />
        )}

        {!isEditMode && <LeadChatLinkButton lead={lead} />}

        {!isEditMode && aiEnabled && !isArchived && (
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 rounded-lg hover:bg-accent/50"
                style={{
                  backgroundImage:
                    "linear-gradient(160.304deg, rgba(38, 115, 85, 0.01) 110.38%, rgba(38, 115, 85, 0.12) 110.92%)",
                }}
                onClick={startAICall}
                aria-label="Call with Xale AI"
              >
                <Bot className="size-4 text-[var(--color-black-8)]" strokeWidth={1.5} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="end" className="max-w-[240px]">
              <div className="text-[12px] leading-snug">
                <div className="font-semibold mb-0.5">Call with Xale AI</div>
                <div className="text-[var(--color-black-7)]">Xale AI talks to this lead as your telecaller — in Malayalam by default — and writes the call note and follow-up.</div>
              </div>
            </TooltipContent>
          </Tooltip>
        )}

        {!isEditMode && currentTab == "Overview" && !isViewLocked && (
          <Can moduleKey="lead_management" action="edit">
            <Button
              variant="ghost"
              size="icon"
              className="size-8 rounded-lg hover:bg-accent/50"
              style={{
                backgroundImage:
                  "linear-gradient(160.304deg, rgba(38, 115, 85, 0.01) 110.38%, rgba(38, 115, 85, 0.12) 110.92%)",
              }}
              onClick={handleEditMode}
            >
              <Pencil
                className="size-4 text-[var(--color-black-8)]"
                strokeWidth={1.5}
              />
            </Button>
          </Can>
        )}

        {!isEditMode && !isViewLocked && (
          <Can
            checks={[
              { moduleKey: "lead_management", action: "edit" },
              { moduleKey: "lead_management", action: "delete" },
              // The menu also hosts "Delete Deal", governed by the per-stage
              // "Choices → Delete choices" toggle — a role holding ONLY that
              // (no lead edit/delete) must still reach the menu, or the
              // toggle grants a button that never renders.
              ...(activeDealId != null && Number.isFinite(activeDealStageId)
                ? [
                    {
                      moduleKey: "choice_management",
                      action: "delete",
                      stageId: activeDealStageId,
                    },
                  ]
                : []),
            ]}
            match="any"
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 rounded-lg hover:bg-accent/50"
                  style={{
                    backgroundImage:
                      "linear-gradient(160.304deg, rgba(38, 115, 85, 0.01) 110.38%, rgba(38, 115, 85, 0.12) 110.92%)",
                  }}
                >
                  <MoreVertical
                    className="size-4 text-[var(--color-black-8)]"
                    strokeWidth={1.5}
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <Can moduleKey="lead_management" action="edit">
                  {canToggleArchive && (
                    <DropdownMenuItem
                      onClick={handleToggleArchive}
                      disabled={archiveMutation.isPending}
                      className="cursor-pointer"
                    >
                      {isArchived ? (
                        <ArchiveRestore className="mr-2 h-4 w-4" />
                      ) : (
                        <Archive className="mr-2 h-4 w-4" />
                      )}
                      <span>
                        {archiveMutation.isPending
                          ? isArchived
                            ? "Restoring…"
                            : "Archiving…"
                          : isArchived
                            ? "Restore from Archive"
                            : "Move to Archive"}
                      </span>
                    </DropdownMenuItem>
                  )}
                </Can>
                {/* Merging removes the other lead, so it rides the delete
                    permission; the server checks the exact stages. */}
                <Can moduleKey="lead_management" action="delete">
                  <DropdownMenuItem
                    onClick={() => setIsMergeOpen(true)}
                    className="cursor-pointer"
                  >
                    <GitMerge className="mr-2 h-4 w-4" />
                    <span>Merge with another lead</span>
                  </DropdownMenuItem>
                </Can>
                <Can moduleKey="lead_management" action="delete">
                  <DropdownMenuItem
                    onClick={() => setIsDeleteDialogOpen(true)}
                    className="cursor-pointer text-[var(--color-error)] focus:text-[var(--color-error)] focus:bg-red-50"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    <span>Delete Lead</span>
                  </DropdownMenuItem>
                </Can>
                {canDeleteActiveDeal && (
                  <DropdownMenuItem
                    onClick={() => setIsDeleteDealDialogOpen(true)}
                    className="cursor-pointer text-[var(--color-error)] focus:text-[var(--color-error)] focus:bg-red-50"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    <span>Delete Deal</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </Can>
        )}

        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="size-8 rounded-lg hover:bg-accent/50"
            style={{
              backgroundImage:
                "linear-gradient(160.304deg, rgba(38, 115, 85, 0.01) 110.38%, rgba(38, 115, 85, 0.12) 110.92%)",
            }}
          >
            <X
              className="size-[20px] text-[var(--colorblack-10)]"
              strokeWidth={2}
            />
          </Button>
        )}
      </div>

      <MergeLeadDialog
        open={isMergeOpen}
        onClose={() => setIsMergeOpen(false)}
        lead={lead?.id != null ? { id: Number(lead.id), name: lead?.name ?? null } : null}
      />

      <ConfirmDialog
        open={isDeleteDealDialogOpen}
        onClose={() => setIsDeleteDealDialogOpen(false)}
        onConfirm={handleDeleteDeal}
        title={`Delete deal "${(child as any)?.name ?? ""}"?`}
        tone="destructive"
        warningTitle={
          isLastDeal
            ? "Critical — this is the lead's LAST deal and it cannot be undone"
            : "This cannot be undone"
        }
        warningDescription={
          isLastDeal
            ? `The deal and its payment records, follow-ups, deadlines and assignment rows are permanently deleted. With no deal left, the lead ${
                lastDealFallbackStageName
                  ? `moves back to "${lastDealFallbackStageName}"`
                  : "moves back to the last stage that doesn't need a deal"
              } and its current stage progress is lost. This is irreversible — re-creating a deal starts it from the first deal stage again.`
            : "The deal and its payment records, follow-ups, deadlines and assignment rows are permanently deleted. If it was the lead's furthest deal, the lead moves to match its next furthest deal."
        }
        confirmLabel="Delete Deal"
        loadingLabel="Deleting…"
        isLoading={isDeletingDeal}
        size="sm"
      />

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0" showCloseButton={false}>
          <DialogHeader className="px-5 pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="size-5 text-[var(--color-error)]" />
              </div>
              <div>
                <DialogTitle asChild>
                  <p className="text-[16px] leading-snug font-semibold text-[var(--color-black-10)] tracking-[-0.32px]">
                    Move &ldquo;{lead?.name}&rdquo; to Trash?
                  </p>
                </DialogTitle>
                <DialogDescription className="text-[13px] text-[var(--color-black-6)] mt-1 tracking-[-0.26px]">
                  The lead can be restored from Settings → System Logs → Trash,
                  but related data below will be permanently deleted.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="px-5 pb-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-3.5">
              <p className="text-[13px] font-medium text-[var(--color-error)] mb-2 tracking-[-0.26px]">
                The following will be permanently deleted and cannot be restored:
              </p>
              <ul className="text-[12.5px] text-[var(--color-black-7)] space-y-1.5 tracking-[-0.25px]">
                <li className="flex items-center gap-2">
                  <span className="size-1.5 rounded-full bg-[var(--color-error)] shrink-0" />
                  Lead source, branch, assignment and stage history
                </li>
                {childCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-1.5 rounded-full bg-[var(--color-error)] shrink-0" />
                    {childCount} application{childCount !== 1 ? "s" : ""} and their deal/payment records
                  </li>
                )}
                {noteCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-1.5 rounded-full bg-[var(--color-error)] shrink-0" />
                    {noteCount} note{noteCount !== 1 ? "s" : ""}
                  </li>
                )}
                {followUpCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-1.5 rounded-full bg-[var(--color-error)] shrink-0" />
                    {followUpCount} follow-up{followUpCount !== 1 ? "s" : ""} and deadlines
                  </li>
                )}
                {whatsappChatCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-1.5 rounded-full bg-[var(--color-error)] shrink-0" />
                    WhatsApp chat history and messages
                  </li>
                )}
                <li className="flex items-center gap-2">
                  <span className="size-1.5 rounded-full bg-[var(--color-error)] shrink-0" />
                  Activity logs, attachments, and all related records
                </li>
              </ul>
            </div>
          </div>

          <DialogFooter className="px-5 pb-5 pt-0 flex justify-end gap-2">
            <Button
              variant="cancel"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteLead}
              disabled={isDeleting}
            >
              {isDeleting ? "Moving to Trash..." : "Move to Trash"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
